import React from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  ColDefBuilder,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  P,
  Tag,
  VStack,
  legacyTextStyles,
  makeDefaultSortableColumnDef,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import { ArtifactHeader } from './ArtifactHeader';

const diagnosticReportFragment = graphql(`
  fragment DiagnosticReport on DiagnosticReports @_unmask {
    __typename
    id
    observations(orderBy: { ordinal: ASC }) {
      __typename
      id
      name
      value
      unit
      referenceRange
      isOutsideReferenceRange
    }
  }
`);

export const patientDiagnosticReportsFragment = graphql(
  `
    fragment PatientDiagnosticReports on Patients @_unmask {
      __typename
      id
      diagnostic_reports(orderBy: { issuedAt: DESC }) {
        __typename
        id
        name
        issuedAt
        conclusion
        ...DiagnosticReport
      }
    }
  `,
  [diagnosticReportFragment],
);

type ReportRow = {
  id: string;
  name: string;
  issuedAt: string;
  outsideRangeCount: number;
  observationsCount?: number;
};

type ObservationRow = {
  id: string;
  name: string;
  value: string;
  referenceRange: string;
  isOutsideReferenceRange: boolean;
};

const observationColumns = new ColDefBuilder<ObservationRow>()
  .defaultSortable('name')
  .colDef({
    ...makeDefaultSortableColumnDef('value', 'Value'),
    cell: ({
      row: {
        original: { value, isOutsideReferenceRange },
      },
    }) => {
      if (isOutsideReferenceRange) {
        return <Tag label={value} color="orange" />;
      }

      return (
        <span className={legacyTextStyles.body({ size: 'm' })}>{value}</span>
      );
    },
  })
  .defaultSortable('referenceRange', 'Reference Range')
  .build();

const DiagnosticReportDetailsModal: React.FC<{
  name: string;
  diagnosticReportId: string;
}> = ({ diagnosticReportId, name }) => {
  const { observations = [] } = useCompleteFragment({
    fragment: diagnosticReportFragment,
    key: { id: diagnosticReportId },
  });

  const rows = (observations ?? []).map<ObservationRow>((observation) => ({
    id: observation.id,
    name: observation.name ?? '',
    value: `${observation.value} ${observation.unit ?? ''}`,
    referenceRange: observation.referenceRange ?? '',
    isOutsideReferenceRange: observation.isOutsideReferenceRange ?? false,
  }));

  return (
    <Dialog>
      <DialogTrigger>
        <P>{name}</P>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-scroll">
        <DialogHeader>
          <DialogTitle>{name}</DialogTitle>
        </DialogHeader>
        <div className="overflow-scroll">
          <DataTable
            columns={observationColumns}
            data={rows}
            isPaginated={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const reportColumns = new ColDefBuilder<ReportRow>()
  .colDef({
    ...makeDefaultSortableColumnDef('name', 'Name'),
    id: 'name',
    cell: ({ row }) => (
      <DiagnosticReportDetailsModal
        name={row.original.name}
        diagnosticReportId={row.original.id}
      />
    ),
  })
  .defaultSortable('observationsCount', 'Observations Count')
  .colDef({
    ...makeDefaultSortableColumnDef(
      'outsideRangeCount',
      'Outside reference range',
    ),
    cell: ({ row }) => {
      const { outsideRangeCount } = row.original;

      if (outsideRangeCount > 0) {
        return <Tag label={outsideRangeCount.toString()} color="orange" />;
      }

      return <span>--</span>;
    },
  })
  .dateSortable('issuedAt', 'Issued At')
  .build();

export interface PatientLabResultsProps {
  patientId: string;
}

export const PatientLabResults: React.FC<PatientLabResultsProps> = ({
  patientId,
}) => {
  const { diagnostic_reports } = useCompleteFragment({
    fragment: patientDiagnosticReportsFragment,
    key: { id: patientId },
  });

  const rows = (diagnostic_reports ?? []).map<ReportRow>((report) => ({
    id: report.id,
    name: report.name ?? '',
    issuedAt: report.issuedAt ?? '',
    observationsCount: report?.observations?.length ?? 0,
    outsideRangeCount:
      report?.observations?.filter((o) => o.isOutsideReferenceRange)?.length ??
      0,
  }));

  return (
    <VStack gap={3}>
      <ArtifactHeader text="Lab Results" />
      <DataTable
        columns={reportColumns}
        data={rows}
        initialSortingState={[
          {
            id: 'issuedAt',
            desc: true,
          },
        ]}
      />
    </VStack>
  );
};
